import { Button, Text } from "@ttc3k/trekker";
import { EditPencil } from "iconoir-react";
import { formatPrice, getResolvedProductSellingConfig } from "shared/utils";
import { getMultilingualString } from "shared/utils";
import { EnumProductPublicationStatus, Product } from "gql/generated";
import { styled } from "styled-system/jsx";
import { Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { Check } from "iconoir-react";

type FeeSummaryCardProps = {
  product: Product;
  isSelected?: boolean;
  displayStatusTag?: boolean;
  onEditClick?: () => void;
  onSelectClick?: () => void;
  /** Lets a selected card be clicked again to deselect it (multi-select lists). */
  onDeselectClick?: () => void;
  /** Name and actions share one row, so the card stays short in long lists. */
  actionsInline?: boolean;
};

const CardContainer = styled(Flex, {
  base: {
    borderRadius: "100",
    bg: "white",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "border.light",
    outlineWidth: "1px",
    outlineStyle: "solid",
    outlineColor: "transparent",
    justifyContent: "space-between",
    overflow: "hidden",
    transitionProperty: "border-color, outline-color",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-in-out",
  },
  variants: {
    visual: {
      default: {},
      selected: {
        borderColor: "border.dark",
        outlineColor: "border.dark",
      },
      draft: {
        borderStyle: "dashed",
        bg: "bg.wa.lightest",
      },
    },
  },
});

export const FeeSummaryCard = ({
  product,
  isSelected,
  onEditClick,
  onSelectClick,
  onDeselectClick,
  actionsInline = false,
}: FeeSummaryCardProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "BOOKING_WIDGET_SETUP",
  });

  const status = product.publicationStatus;
  const isDraft = status === EnumProductPublicationStatus.Draft;

  const sellingConfig = getResolvedProductSellingConfig(
    product as Parameters<typeof getResolvedProductSellingConfig>[0],
  );
  const { options, basePrice } = sellingConfig;
  const priceText = (() => {
    if (options.length === 0) {
      return `$${formatPrice(basePrice)}`;
    }
    const lowestPrice = Math.min(...options.map(o => o.price));
    if (options.length === 1) {
      return `$${formatPrice(lowestPrice)}`;
    }
    return `${t("STEPS.1.STARTING_AT")} $${formatPrice(lowestPrice)}`;
  })();

  const actions = (
    <Flex gap={"100"} flexShrink={0}>
      {onSelectClick && (
        <Button
          mode={isSelected ? "dark" : "light"}
          Icon={isSelected ? Check : undefined}
          _hover={isSelected ? { bg: "bg.darkest" } : undefined}
          cursor={isSelected && !onDeselectClick ? "unset" : undefined}
          onClick={isSelected ? onDeselectClick : onSelectClick}
          disabled={status !== EnumProductPublicationStatus.Approved}
          size={"sm"}
        >
          {isSelected ? t("PRODUCT.SELECTED") : t("PRODUCT.SELECT")}
        </Button>
      )}
      {onEditClick && (
        <Button
          mode={"light"}
          Icon={EditPencil}
          onClick={onEditClick}
          size={"sm"}
        />
      )}
    </Flex>
  );

  return (
    <CardContainer
      visual={isSelected ? "selected" : isDraft ? "draft" : "default"}
      w="full"
    >
      <Flex
        flexDirection={"column"}
        gap={"50"}
        p={actionsInline ? "200" : "300"}
        w="full"
      >
        <Flex
          alignItems={actionsInline ? "flex-start" : "center"}
          justifyContent={"space-between"}
          gap={"200"}
        >
          {actionsInline ? (
            <Text
              visual={"smallSemiBold"}
              color={"text.dark"}
              lineClamp={2}
              minW={0}
            >
              {getMultilingualString(product?.name ?? {})}
            </Text>
          ) : (
            <Text visual={"bodySemiBold"} color={"text.dark"} truncate>
              {getMultilingualString(product?.name ?? {})}
            </Text>
          )}
          {actionsInline && actions}
        </Flex>
        <Text visual={"smallMedium"} color={"text.light"}>
          {priceText}
        </Text>
        {!actionsInline && actions}
      </Flex>
    </CardContainer>
  );
};
